/**
 * Relink Infinite Options add-ons whose `_io_order_group` no longer matches the
 * parent line, so Octopus imports them as children instead of orphan lines
 * (ENG-824). Runs on cart load, before checkout.
 *
 * Ownership is adjacency — hidden IO children belong to the nearest preceding
 * shippable line — the same model main-cart.liquid already uses to derive
 * `derived_io_order_group` and to remove children alongside their parent.
 */
(() => {
  'use strict';

  const ATTEMPTS_KEY = 'ioCartRepairAttempts';
  const MAX_ATTEMPTS = 2;
  const HIDDEN = new Set(['OPTIONS_HIDDEN_PRODUCT', 'PPLR_HIDDEN_PRODUCT']);

  const cartRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const props = (item) => item.properties || {};
  const isHidden = (item) => HIDDEN.has(item.product_type);

  const fetchCart = async () => {
    const response = await fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart.js failed');
    return response.json();
  };

  const postJson = async (path, body) => {
    const response = await fetch(cartRoot() + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(path + ' failed');
    return response.json();
  };

  const nearestOwner = (items, index) => {
    for (let i = index - 1; i >= 0; i--) {
      if (!isHidden(items[i])) return items[i];
    }
    return null;
  };

  const orphanGroupsUnder = (items, parentIndex, parentGroups) => {
    const groups = new Set();
    for (let i = parentIndex + 1; i < items.length; i++) {
      if (!isHidden(items[i])) break;
      const group = props(items[i])._io_parent_order_group;
      if (group && !parentGroups.has(String(group))) groups.add(String(group));
    }
    return groups;
  };

  const ownerHasTwin = (items, owner, orphan) => {
    const ownerGroup = props(owner)._io_order_group;
    if (!ownerGroup) return false;
    const field = props(orphan)._io_field_name;
    return items.some(
      (other) =>
        other.key !== orphan.key &&
        other.variant_id === orphan.variant_id &&
        props(other)._io_field_name === field &&
        props(other)._io_parent_order_group === ownerGroup
    );
  };

  const buildPlan = (cart) => {
    const items = cart.items || [];
    const parentGroups = new Set();
    const childGroups = new Set();

    for (const item of items) {
      const p = props(item);
      if (!isHidden(item) && p._io_order_group) parentGroups.add(String(p._io_order_group));
      if (p._io_parent_order_group) childGroups.add(String(p._io_parent_order_group));
    }

    // Leftover children from an earlier attempt: the owner already has this
    // add-on linked, so the orphan copy is an overcharge.
    const removeKeys = [];
    items.forEach((item, index) => {
      const group = props(item)._io_parent_order_group;
      if (!group || parentGroups.has(String(group))) return;
      const owner = nearestOwner(items, index);
      if (owner && ownerHasTwin(items, owner, item)) removeKeys.push(item.key);
    });

    // A parent may claim one orphan group only, and a group only one parent.
    const claimers = new Map();
    items.forEach((item, index) => {
      if (isHidden(item)) return;
      // Never overwrite a group that children are currently linked to.
      const ownGroup = props(item)._io_order_group;
      if (ownGroup && childGroups.has(String(ownGroup))) return;

      const orphans = orphanGroupsUnder(items, index, parentGroups);
      if (orphans.size !== 1) return;

      const group = orphans.values().next().value;
      claimers.set(group, claimers.has(group) ? null : item.key);
    });

    const restamps = [];
    for (const [group, key] of claimers) {
      if (key) restamps.push({ key, group });
    }

    return { removeKeys, restamps };
  };

  // Rebuilt from server truth before every write: an earlier restamp in this
  // pass, or another tab, may have resolved the mismatch already.
  const applyIfStillPlanned = async (predicate, write) => {
    const cart = await fetchCart();
    const plan = buildPlan(cart);
    if (!predicate(plan)) return false;
    await write(cart);
    return true;
  };

  const restampParent = (key, group) =>
    applyIfStillPlanned(
      (plan) => plan.restamps.some((row) => row.key === key && row.group === group),
      async (cart) => {
        const line = cart.items.find((item) => item.key === key);
        // change.js replaces the whole properties object, so merge onto current.
        await postJson('cart/change.js', {
          id: key,
          quantity: line.quantity,
          properties: Object.assign({}, props(line), { _io_order_group: group }),
        });
      }
    );

  const removeOrphan = (key) =>
    applyIfStillPlanned(
      (plan) => plan.removeKeys.includes(key),
      () => postJson('cart/update.js', { updates: { [key]: 0 } })
    );

  const run = async () => {
    const attempts = Number(sessionStorage.getItem(ATTEMPTS_KEY)) || 0;
    if (attempts >= MAX_ATTEMPTS) {
      sessionStorage.removeItem(ATTEMPTS_KEY);
      return;
    }

    try {
      const plan = buildPlan(await fetchCart());
      let repaired = false;

      for (const row of plan.restamps) {
        if (await restampParent(row.key, row.group)) repaired = true;
      }
      for (const key of plan.removeKeys) {
        if (await removeOrphan(key)) repaired = true;
      }

      if (!repaired) {
        sessionStorage.removeItem(ATTEMPTS_KEY);
        return;
      }

      sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
      window.location.reload();
    } catch (err) {
      console.error('[io-cart-repair] failed', err);
      sessionStorage.removeItem(ATTEMPTS_KEY);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
